import { Component, computed, inject } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgDiagramViewportService } from 'ng-diagram';
import {
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
  ZOOM_TO_FIT_PADDING,
} from './org-chart.model';

const BUTTON_CLASS =
  'flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40';

/**
 * Zoom / re-centre panel overlaid on the organigram.
 *
 * Rendered as a sibling of `<ng-diagram>` rather than inside it, so clicks never
 * reach the diagram's pan handling. `zoom()` takes a *relative* factor, so each
 * press converts an absolute target scale back into a factor — matching how
 * ng-diagram's own zoom controls step.
 */
@Component({
  selector: 'org-chart-zoom-controls',
  standalone: true,
  imports: [TranslocoPipe],
  template: `
    <div
      class="flex items-center gap-0.5 rounded-lg border border-border bg-card p-1 shadow-sm"
    >
      <button
        type="button"
        [class]="buttonClass"
        [disabled]="!canZoomOut()"
        [title]="'organigram.zoom.out' | transloco"
        [attr.aria-label]="'organigram.zoom.out' | transloco"
        (click)="zoomOut()"
      >
        <span class="material-icons text-base" aria-hidden="true">remove</span>
      </button>

      <button
        type="button"
        class="min-w-11 rounded-md px-1 py-1 text-xs font-medium tabular-nums text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        [title]="'organigram.zoom.reset' | transloco"
        (click)="resetZoom()"
      >
        {{ zoomPercent() }}%
      </button>

      <button
        type="button"
        [class]="buttonClass"
        [disabled]="!canZoomIn()"
        [title]="'organigram.zoom.in' | transloco"
        [attr.aria-label]="'organigram.zoom.in' | transloco"
        (click)="zoomIn()"
      >
        <span class="material-icons text-base" aria-hidden="true">add</span>
      </button>

      <span class="mx-0.5 h-5 w-px bg-border" aria-hidden="true"></span>

      <button
        type="button"
        [class]="buttonClass"
        [title]="'organigram.zoom.fit' | transloco"
        [attr.aria-label]="'organigram.zoom.fit' | transloco"
        (click)="centre()"
      >
        <span class="material-icons text-base" aria-hidden="true"
          >center_focus_strong</span
        >
      </button>
    </div>
  `,
})
export class OrgChartZoomControlsComponent {
  readonly #viewport = inject(NgDiagramViewportService);

  readonly buttonClass = BUTTON_CLASS;

  readonly zoomPercent = computed(() =>
    Math.round(this.#viewport.scale() * 100),
  );
  readonly canZoomIn = computed(() => this.#viewport.scale() < ZOOM_MAX);
  readonly canZoomOut = computed(() => this.#viewport.scale() > ZOOM_MIN);

  zoomIn(): void {
    this.#zoomTo(Math.min(this.#viewport.scale() + ZOOM_STEP, ZOOM_MAX));
  }

  zoomOut(): void {
    this.#zoomTo(Math.max(this.#viewport.scale() - ZOOM_STEP, ZOOM_MIN));
  }

  resetZoom(): void {
    this.#zoomTo(1);
  }

  /** Fit the whole tree back into view. */
  centre(): void {
    this.#viewport.zoomToFit({ padding: ZOOM_TO_FIT_PADDING });
  }

  #zoomTo(targetScale: number): void {
    const current = this.#viewport.scale();
    if (!current || targetScale === current) return;
    this.#viewport.zoom(targetScale / current);
  }
}
